// frontend/app/components/lessonPlans/present/presenterChannel.js
//
// The wire between the lesson on the projector and the presenter view on the
// teacher's laptop.
//
// WHY A SECOND WINDOW EXISTS AT ALL. Present mode was designed for one mirrored
// screen, where whatever the teacher reads thirty students read with them — so
// the guidance and the answer key hide behind the N toggle, and PresentNotes
// says out loud that it is "visible to the room". A second screen dissolves
// that compromise: the lesson stays full-screen on the projector, the notes go
// to the laptop, and nothing has to be hidden any more. That is the whole
// feature, and this file is how the two halves stay in step.
//
// WHY BroadcastChannel RATHER THAN A WINDOW HANDLE. The opener does hold a
// reference to the popup, but the traffic runs both ways — the popup drives the
// lesson as much as it follows it — and a window handle gives no inbound path
// without reaching across into another document's globals. BroadcastChannel is
// symmetric, same-origin by construction, and survives either side re-rendering.
//
// WHY EVERY CALL HERE IS GUARDED. A classroom is the worst place to discover
// that a browser is one of the few without BroadcastChannel. Nothing in this
// module throws: an unavailable channel comes back as null, and the caller
// degrades to a presenter window that shows this moment's notes and says
// plainly that it will not follow the lesson. A teacher can work with that. An
// exception thrown into the render of a projected lesson, they cannot.

import { getPhaseMeta } from "../lessonPlanTheme";
import { revealableBlockIds, stepNotes } from "./presentRules";

/**
 * One fixed name for the whole origin, because both sides have to derive it
 * without ever having spoken to each other.
 */
export const PRESENTER_CHANNEL_NAME = "saras-lesson-presenter";

/**
 * What either side may say. Five verbs, and each one names who sends it:
 *
 *   STATE   main → presenter   the whole current picture, on every change
 *   ENDED   main → presenter   the lesson is over; close yourself
 *   HELLO   presenter → main   I just opened, tell me where we are
 *   INTENT  presenter → main   the teacher pressed one of my controls
 *   BYE     presenter → main   I am being closed from my own side
 *
 * STATE is a full snapshot rather than a diff. Snapshots make a window opened
 * mid-lesson identical to one opened at the start, and there is no ordering or
 * missed-message bug a snapshot can accumulate — the next step wipes the slate.
 */
export const PRESENTER_MESSAGE = {
  STATE: "presenter:state",
  ENDED: "presenter:ended",
  HELLO: "presenter:hello",
  INTENT: "presenter:intent",
  BYE: "presenter:bye",
};

/**
 * The four moves the presenter window may ask for.
 *
 * REVEAL IS NOT ADVANCE, AND BOTH ARE NEEDED. On the main surface Space reveals
 * the next answer and — once they are all shown — moves on, because one clicker
 * button has to do the obvious thing. A labelled button in a second window is
 * not that button: a teacher pressing "Reveal" while glancing at their laptop
 * must never find the projector has jumped to the next slide. So the buttons
 * send `reveal` and `next` separately.
 *
 * `advance` exists for the clicker. Its USB receiver is plugged into the
 * laptop, which is the machine the presenter window is on, so whichever of the
 * two windows has focus gets the keypress — and the big button has to mean the
 * same thing either way. The popup binds the same keys as usePresentKeys and
 * forwards Space as `advance`, so a teacher who clicked the presenter window
 * last is not holding a clicker that has quietly stopped working.
 */
export const PRESENTER_INTENTS = ["previous", "next", "reveal", "advance"];

/** True where the browser can carry this traffic at all. */
export const supportsPresenterChannel = () =>
  typeof window !== "undefined" && typeof window.BroadcastChannel === "function";

/**
 * A channel, or null where there is none to be had.
 *
 * Constructing one can throw as well as be absent — a sandboxed iframe with an
 * opaque origin refuses — so the try is not defensive padding.
 */
export const openPresenterChannel = (name = PRESENTER_CHANNEL_NAME) => {
  if (!supportsPresenterChannel()) return null;
  try {
    return new window.BroadcastChannel(name);
  } catch (err) {
    return null;
  }
};

/** Best-effort send. Returns whether it went. Never throws. */
export const postPresenterMessage = (channel, message) => {
  if (!channel || typeof channel.postMessage !== "function") return false;
  try {
    channel.postMessage(message);
    return true;
  } catch (err) {
    // A closed channel throws InvalidStateError, and that happens legitimately
    // in teardown — where the last thing we do is tell the popup we are going.
    return false;
  }
};

/** Best-effort close, for every teardown path. */
export const closePresenterChannel = (channel) => {
  if (!channel || typeof channel.close !== "function") return;
  try {
    channel.close();
  } catch (err) {
    /* already closed */
  }
};

/**
 * A per-lesson id carried on every message.
 *
 * BroadcastChannel is scoped to the origin, not to a tab — two teachers on one
 * shared staffroom machine, or one teacher with yesterday's lesson still open
 * in a background tab, would otherwise drive each other's slides. The channel
 * name stays fixed (both sides must derive it cold) and the id does the
 * separating: a message that is not ours is dropped, silently, on both sides.
 */
export const makePresenterSessionId = () =>
  "p" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

/** Is this message ours, and shaped like something we know how to read? */
export const isPresenterMessageFor = (sessionId, data) =>
  !!data &&
  typeof data === "object" &&
  data.sessionId === sessionId &&
  typeof data.type === "string";

/**
 * Every word the presenter window says.
 *
 * The popup is plain HTML with no access to the app's i18n, so its copy has to
 * travel with its state — and it does have to travel. Present mode ships in
 * English, Gujarati, Telugu and Portuguese, and every other string on this
 * surface goes through t(); a teacher whose lesson is in Gujarati would
 * otherwise get their own notes framed in English headings.
 *
 * Placeholders follow the app's {name} convention so a translation is free to
 * reorder them.
 */
export const PRESENTER_LABELS = {
  presenterTitle: "Presenter",
  step: "Step {n} of {total}",
  minutes: "min",
  whatYouDo: "What you do",
  // The workspace says "What students do", because a student is reading it
  // about themselves. Here a teacher is reading it about the room in front of
  // them — the same sentence has to name who is doing what. PresentNotes makes
  // exactly this change for exactly this reason.
  withClass: "Do this with the class",
  teacherTip: "Teacher tip",
  noNotes: "No teacher notes were written for this step.",
  comingNext: "Coming next",
  lastStep: "Last step",
  endOfLesson: "This is the end of the lesson.",
  back: "Back",
  reveal: "Reveal",
  next: "Next",
  nothingToReveal: "Nothing to reveal on this step.",
  revealed: "{shown} of {total} answers revealed on the projector.",
  keys: "Arrows or a clicker to move · Space to reveal · Esc closes this window only",
  offline:
    "This browser cannot link the two windows, so these notes will not follow the lesson. Use the controls on the lesson screen; close this window when you are done.",
};

/** The same dictionary, run through the caller's translator. */
export const buildPresenterLabels = (t) => {
  if (typeof t !== "function") return { ...PRESENTER_LABELS };
  return Object.keys(PRESENTER_LABELS).reduce((out, key) => {
    const translated = t(PRESENTER_LABELS[key]);
    out[key] =
      typeof translated === "string" && translated.length > 0
        ? translated
        : PRESENTER_LABELS[key];
    return out;
  }, {});
};

const stepSummary = (step) => {
  if (!step) return null;
  const phase = getPhaseMeta(step.phase);
  return {
    title: typeof step.title === "string" ? step.title : "",
    phaseLabel: phase.label,
    phaseAccent: phase.accent,
    estimatedMinutes:
      Number(step.estimated_minutes) > 0 ? Number(step.estimated_minutes) : null,
  };
};

/**
 * Everything the presenter window draws, as one plain serializable object.
 *
 * Built here rather than inside the hook so it can be unit-tested without a
 * browser, and so the popup's first paint and its hundredth update are provably
 * the same shape — both go through one renderer, which is why a window opened
 * on step 9 shows step 9 and not step 1.
 *
 * `notes` is exactly what the N panel shows. The whole point of the second
 * screen is that this no longer has to be a toggle, so the presenter window
 * draws it unconditionally and has no way to hide it.
 */
export const buildPresenterState = ({
  lesson,
  steps = [],
  index = 0,
  revealCount = 0,
  startedAt = null,
  sessionId = null,
  live = true,
  isDarkMode = true,
  labels = PRESENTER_LABELS,
} = {}) => {
  const list = Array.isArray(steps) ? steps : [];
  const total = list.length;
  const safeIndex = Math.max(0, Math.min(Number(index) || 0, Math.max(0, total - 1)));
  const step = total > 0 ? list[safeIndex] : null;

  const revealTotal = revealableBlockIds(step).length;
  const shown = Math.max(0, Math.min(Number(revealCount) || 0, revealTotal));
  const current = stepSummary(step);

  return {
    sessionId,
    live: !!live,
    theme: isDarkMode ? "dark" : "light",
    labels: { ...PRESENTER_LABELS, ...(labels || {}) },
    lessonTitle: typeof lesson?.title === "string" ? lesson.title : "",
    stepIndex: safeIndex,
    total,
    // The teacher reads "Step 4 of 9", never "index 3".
    stepNumber: total > 0 ? safeIndex + 1 : 0,
    phaseLabel: current?.phaseLabel || "",
    phaseAccent: current?.phaseAccent || "#64748b",
    stepTitle: current?.title || "",
    estimatedMinutes: current?.estimatedMinutes || null,
    notes: stepNotes(step),
    revealTotal,
    revealCount: shown,
    revealsLeft: Math.max(0, revealTotal - shown),
    // What is coming, so the teacher can land this step on a sentence that
    // leads into the next one instead of reading it cold off the wall.
    next: safeIndex + 1 < total ? stepSummary(list[safeIndex + 1]) : null,
    atStart: safeIndex <= 0,
    atEnd: total === 0 || safeIndex >= total - 1,
    // The popup runs its own clock off this epoch. Same machine, same clock, so
    // there is nothing to keep in sync — and a ticking number sent over the
    // channel once a second would be a message a second for no reason.
    startedAt: Number(startedAt) > 0 ? Number(startedAt) : null,
  };
};
